import { RudderEvent } from 'constants/rudderEvents'
import { useIsOsanoCmpActive } from 'context/common/OsanoCmpContext'
import { subscribeToOsanoConsentRevocation } from 'lib/osanoConsentRevocation'
import { useEffect } from 'react'
import logEvent from 'utils/logEvent'

/** Wait for an already-queued `osano_consent_saved` track to leave the page. */
export const CONSENT_SAVED_FLUSH_MS = 300

/** Fallback if Rudder's track callback never runs after we emit consent_saved. */
export const CONSENT_SAVED_RELOAD_FALLBACK_MS = 500

/**
 * One reload scheduler per subscription so consecutive saves share state: a
 * pending reload can be replaced (Analytics revoke wins over an earlier
 * Marketing one) and the page never reloads twice.
 */
function createReloadScheduler(): {
  reload: () => void
  schedule: (delayMs: number) => void
  cancel: () => void
} {
  let reloaded = false
  let timer: ReturnType<typeof setTimeout> | undefined

  const cancel = (): void => {
    if (timer) {
      clearTimeout(timer)
      timer = undefined
    }
  }

  const reload = (): void => {
    if (reloaded) {
      return
    }

    reloaded = true
    cancel()
    window.location.reload()
  }

  const schedule = (delayMs: number): void => {
    cancel()
    timer = setTimeout(reload, delayMs)
  }

  return { reload, schedule, cancel }
}

/**
 * Reloads the page when the visitor saves Osano preferences that revoke a
 * previously granted category. Already-running vendor scripts (CallRail,
 * Contentsquare, Heap, ad pixels) cannot be unloaded, so a fresh document is
 * the only reliable way to honor the change.
 *
 * When Analytics goes ACCEPT → DENY, `useOsanoConsentEvents` skips
 * `osano_consent_saved` (it only sends while ANALYTICS is ACCEPT). Emit that
 * event here before reload so Data still sees the revoke, while Rudder is
 * still loaded. Other revokes (Marketing/Personalization, or Analytics that
 * was already DENY) keep the delayed reload only.
 *
 * Must render under OsanoCmpProviderSyncedToAuth so it re-subscribes when
 * Osano activation flips (e.g. support login/logout).
 */
const useReloadOnConsentRevocation = (): void => {
  const isOsanoCmpActive = useIsOsanoCmpActive()

  useEffect(() => {
    const scheduler = createReloadScheduler()
    let analyticsPriority = false

    const unsubscribe = subscribeToOsanoConsentRevocation(
      (consent?, previous?) => {
        const next = consent ?? window.Osano?.cm?.getConsent?.() ?? {}
        const analyticsRevoked =
          previous?.ANALYTICS === 'ACCEPT' && next.ANALYTICS === 'DENY'

        if (analyticsRevoked && globalThis.rudderanalytics) {
          analyticsPriority = true
          // Replaces any pending Marketing reload. Register the fallback
          // first so a synchronous tracking failure cannot leave the page
          // without a reload; the scheduler dedupes it against the callback.
          scheduler.schedule(CONSENT_SAVED_RELOAD_FALLBACK_MS)
          logEvent(
            RudderEvent.OSANO_CONSENT_SAVED,
            next,
            undefined,
            scheduler.reload
          )

          return
        }

        // Never shorten an in-flight Analytics flush.
        if (!analyticsPriority) {
          scheduler.schedule(CONSENT_SAVED_FLUSH_MS)
        }
      }
    )

    // Don't cancel a pending reload here: the visitor already saved a revoke.
    return unsubscribe
  }, [isOsanoCmpActive])
}

export default useReloadOnConsentRevocation
